import { TAG_STYLE } from 'transfix-ui/components/Tag/TagTypes'
import { capitalize } from 'transfix-ui/core'
import { ICarrierApplicationStates, IMotorCarrierComplianceFlag } from 'types/graphqlTypes'
import {
  AlertFilledIcon,
  CheckFilledIcon,
  ClockIcon,
  InfoIcon,
  WarningFilledIcon,
} from 'transfix-icons'
import { TDecision, TRules } from './RulesManualReviewModal/types'
import {
  ALL_RULE_KEYS,
  ALWAYS_OVERRIDEABLE_RULE_KEYS,
  FAIL_RULES,
  HARD_FAIL_RULE_KEYS,
  INCOMPLETE_RULES,
  PASS_RULES,
  SOFT_FAIL_RULE_KEYS,
  TITLECASE_LOWERCASE_WORDS,
  TRANSFIX_RULE_KEYS,
} from './constants'
import { IRulesAssessmentFragment } from './graphql/RulesAssessmentFragment'
import { TBadgeColor } from './types'

const shouldShowRuleInHardFail = (rule: IRulesAssessmentFragment) => {
  const isResultFail = rule.result === 'fail'
  const isOverrideResultFail = rule?.override?.result === 'fail'
  const isHardFailRule = HARD_FAIL_RULE_KEYS.includes(rule.ruleName)

  return (isHardFailRule && isResultFail) || isOverrideResultFail
}

const shouldShowRuleInSoftFail = (rule: IRulesAssessmentFragment) => {
  const isResultFail = rule.result === 'fail'
  const isOverrideResultPass = rule?.override?.result === 'pass'
  const isSoftFailRule = SOFT_FAIL_RULE_KEYS.includes(rule.ruleName)

  return isSoftFailRule && isResultFail && !isOverrideResultPass
}

const shouldShowRuleInPending = (rule: IRulesAssessmentFragment) => {
  const { override, result, ruleName } = rule

  // Show any incomplete no override rule in pending
  const isRuleResultIncomplete = result === 'incomplete'
  const isOverrideExist = !!override?.result

  if (isRuleResultIncomplete && !isOverrideExist) {
    return true
  }

  const isPendingRule = INCOMPLETE_RULES.includes(ruleName)
  const isResultNotPass = result !== 'pass'
  const isOverridePass = override?.result === 'pass'

  const isResultNotPassAndNoOverride = isResultNotPass && !isOverrideExist
  const isOverrideAndNotPass = isOverrideExist && !isOverridePass

  return isPendingRule && (isResultNotPassAndNoOverride || isOverrideAndNotPass)
}

export const sortRules = (rules: IRulesAssessmentFragment[]) => {
  const sortedRules = {
    hardFail: [] as IRulesAssessmentFragment[],
    softFail: [] as IRulesAssessmentFragment[],
    pending: [] as IRulesAssessmentFragment[],
    passed: [] as IRulesAssessmentFragment[],
  }

  const missingRules = ALL_RULE_KEYS.filter(
    ruleName => !rules.find(rule => rule.ruleName === ruleName)
  )

  const missingRulesAsPending = missingRules.map(ruleName => ({
    ruleName,
    result: 'incomplete',
    override: null,
  }))

  const rulesWithMissingAsPending = [...rules, ...missingRulesAsPending].filter(
    rule => rule.ruleName !== 'sets_requiring_override_file'
  )

  const rulesWithoutDuplicateNames = rulesWithMissingAsPending.filter(
    (rule, index, self) => self.findIndex(r => r.ruleName === rule.ruleName) === index
  )

  const rulesWithoutExtraRules = rulesWithoutDuplicateNames.filter(rule =>
    ALL_RULE_KEYS.find(ruleName => ruleName === rule.ruleName)
  )

  rulesWithoutExtraRules.forEach((rule: IRulesAssessmentFragment) => {
    if (shouldShowRuleInPending(rule)) {
      return sortedRules.pending.push(rule)
    }
    if (shouldShowRuleInHardFail(rule)) {
      return sortedRules.hardFail.push(rule)
    }
    if (shouldShowRuleInSoftFail(rule)) {
      return sortedRules.softFail.push(rule)
    }
    return sortedRules.passed.push(rule)
  })

  return sortedRules
}

const titleCaseWord = (match: string) => {
  if (TITLECASE_LOWERCASE_WORDS.includes(match)) {
    return match
  }
  return capitalize(match)
}

export const titleCaseRuleName = (name: string) => {
  return name
    .toLowerCase()
    .replace(/\b(has|is)_/g, '')
    .replaceAll(/_/g, ' ')
    .replaceAll(/\b(bipd|vin|dot|mc|oos|bca|ein)\b/g, match => match.toUpperCase())
    .replaceAll(/[a-z]+/g, titleCaseWord)
    .trim()
}

export const getTagStyle = (title: string, isOverride?: boolean) => {
  if (isOverride) {
    return TAG_STYLE.info
  }
  switch (title) {
    case 'Passed':
      return TAG_STYLE.success
    case 'Soft Fail':
      return TAG_STYLE.warning
    case 'Hard Fail':
      return TAG_STYLE.error
    default:
      return TAG_STYLE.disabled
  }
}

export const getTagIcon = (title: string) => {
  switch (title) {
    case 'Passed':
      return CheckFilledIcon
    case 'Soft Fail':
      return AlertFilledIcon
    case 'Hard Fail':
      return WarningFilledIcon
    case 'Pending':
      return ClockIcon
    default:
      return InfoIcon
  }
}

export const getRuleCountBadgeColorFromResult = (title: string, count: number): TBadgeColor => {
  if (!count) {
    return 'gray'
  }
  switch (title) {
    case 'Passed':
      return 'green'
    case 'Soft Fail':
      return 'orange'
    case 'Hard Fail':
      return 'red'
    default:
      return 'gray'
  }
}

export const getStateTagStyle = (
  state: ICarrierApplicationStates | null,
  flags?: Partial<IMotorCarrierComplianceFlag>[] | null
) => {
  if (flags?.length) {
    return TAG_STYLE.error
  }

  if (!state) {
    return TAG_STYLE.dark
  }

  switch (state) {
    case ICarrierApplicationStates.application_submitted:
      return TAG_STYLE.dark

    case ICarrierApplicationStates.activated:
      return TAG_STYLE.success

    case ICarrierApplicationStates.new:
    case ICarrierApplicationStates.in_review:
    case ICarrierApplicationStates.expired_insurance:
      return TAG_STYLE.warning

    default:
      return TAG_STYLE.error
  }
}

export const getPassRules = (rules: TRules) => {
  const allRules = [...rules.passed, ...rules.pending, ...rules.hardFail, ...rules.softFail]

  return PASS_RULES.filter(rule => {
    const assessmentRule = allRules.find(({ ruleName }) => ruleName === rule)
    const { override } = assessmentRule || {}

    const isPassed = rules.passed.find(({ ruleName }) => ruleName === rule)
    const isSoftFail = rules.softFail.find(({ ruleName }) => ruleName === rule)
    const isOverridenFail = override?.result === 'fail'
    const isAlwaysOverrideable = ALWAYS_OVERRIDEABLE_RULE_KEYS.includes(rule)

    return !isPassed && (isAlwaysOverrideable || isSoftFail || isOverridenFail)
  })
}

export const getFailRules = (rules: TRules) => {
  const allRules = [...rules.passed, ...rules.pending, ...rules.hardFail, ...rules.softFail]

  return FAIL_RULES.filter(rule => {
    const assessmentRule = allRules.find(({ ruleName }) => ruleName === rule)
    const { override } = assessmentRule || {}

    const isHardFail = rules.hardFail.find(({ ruleName }) => ruleName === rule)
    const isSoftFail = rules.softFail.find(({ ruleName }) => ruleName === rule)
    const isAlwaysOverrideable = ALWAYS_OVERRIDEABLE_RULE_KEYS.includes(rule)
    const isOverridenPass = override?.result === 'pass'
    const isPending = rules.pending.find(({ ruleName }) => ruleName === rule)
    const isTransfixRule = TRANSFIX_RULE_KEYS.includes(rule)
    const isPendingTransfixRule = isTransfixRule && isPending
    return (
      !isPendingTransfixRule &&
      !isHardFail &&
      (isAlwaysOverrideable || isSoftFail || isOverridenPass)
    )
  })
}

export const getIncompleteRules = (rules: TRules) => {
  const allRules = [...rules.passed, ...rules.pending, ...rules.hardFail, ...rules.softFail]

  return INCOMPLETE_RULES.filter(rule => {
    const assessmentRule = allRules.find(({ ruleName }) => ruleName === rule)
    const { override } = assessmentRule || {}

    const isHardFail = rules.hardFail.find(({ ruleName }) => ruleName === rule)
    const isSoftFail = rules.softFail.find(({ ruleName }) => ruleName === rule)
    const isAlwaysOverrideable = ALWAYS_OVERRIDEABLE_RULE_KEYS.includes(rule)
    const isOverridenPass = override?.result === 'pass'
    const isPending = rules.pending.find(({ ruleName }) => ruleName === rule)
    const isTransfixRule = TRANSFIX_RULE_KEYS.includes(rule)
    const isPendingTransfixRule = isTransfixRule && isPending

    // Admin rule is overridable even if it is already in incomplete
    const isVerifiedAdminRule = rule === 'Is Verified Admin'
    const isResultFail = assessmentRule?.result === 'fail'
    const isNoOverride = !override?.result
    const adminRuleException = isVerifiedAdminRule && isResultFail && isNoOverride

    return (
      (!isPendingTransfixRule &&
        !isHardFail &&
        (isAlwaysOverrideable || isSoftFail || isOverridenPass)) ||
      adminRuleException
    )
  })
}

export const getDecisionRules = (decision: TDecision, rules: TRules) => {
  switch (decision) {
    case 'pass':
      return getPassRules(rules)

    case 'fail':
      return getFailRules(rules)

    case 'incomplete':
      return getIncompleteRules(rules)

    default:
      return []
  }
}
